import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { MAX_ROWS_PER_PAGE } from 'src/constants';
import {
  useGetPaginatedLocations,
  useModal,
  useParamQuery,
  useViewChannels,
} from 'src/hooks';
import { ILocationListing, LayoutTypes } from 'src/interfaces';
import { formatQuery } from 'src/utils';

interface UseLocationTabOptions {
  defaultPageSize?: number;
}

export const useLocationTab = ({
  defaultPageSize = MAX_ROWS_PER_PAGE,
}: UseLocationTabOptions = {}) => {
  const { onChangeParams, getParamsValue, activeFilters, onChangeParamsKey } =
    useParamQuery({
      sort: ['name'],
      page: ['1'],
      pageSize: [`${defaultPageSize}`],
    });
  const location = useLocation();

  useEffect(() => {
    const rowsPerPageValue = getParamsValue('rows_per_page')?.[0];
    if (rowsPerPageValue && !getParamsValue('pageSize')?.[0]) {
      onChangeParamsKey('rows_per_page', 'pageSize');
    } else if (rowsPerPageValue) {
      onChangeParams('rows_per_page', []);
    }
  }, [getParamsValue, onChangeParams, onChangeParamsKey]);

  const pageParam = Number(getParamsValue('page')?.[0] || 1);
  const pageSizeFromUrl = getParamsValue('pageSize')?.[0];
  const rowsPerPageFromUrl = getParamsValue('rows_per_page')?.[0];
  const rowsPerPageParam =
    Number(pageSizeFromUrl || rowsPerPageFromUrl) || defaultPageSize;
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const assignLocationGroupModal = useModal();
  const confirmLocationGroupModal = useModal();
  const previewLocationGroupsModal = useModal();

  const hasUnassigned = location.pathname?.includes('unassigned')
    ? true
    : false;

  const { data: locationTabData, isLoading: isLocationTabDataLoading } =
    useGetPaginatedLocations(formatQuery(location.search), hasUnassigned);
  const { data: channels = [] } = useViewChannels();

  const totalPages = locationTabData?.totalPages ?? 0;
  const totalCount = locationTabData?.totalCount ?? 0;

  const locationsContainOutdoorType = useMemo(
    () =>
      locationTabData?.locations?.filter((location) =>
        location.channels?.some(
          (locationChannel: unknown) =>
            channels.find(
              (channel) => channel.channel_name === (locationChannel as string),
            )?.layoutType?.name === LayoutTypes.OUTDOOR,
        ),
      ),
    [channels, locationTabData?.locations],
  );

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    onChangeParams('page', [`${value}`]);
  };

  const onRowsPerPageChange = (value: number) => {
    onChangeParams('pageSize', [`${value}`]);
    onChangeParams('page', ['1']);
  };

  const selectedSortOption: SortMenuOption = {
    option: getParamsValue('sort')[0]?.replace('-', '') as SortOptions,
    order: getParamsValue('sort')[0]?.includes('-') ? 'desc' : 'asc',
  };

  const onSortChangeHandle = (selectedOption: {
    option: SortOptions;
    order: 'desc' | 'asc';
  }) => {
    onChangeParams('sort', [
      `${selectedOption.order === 'desc' ? '-' : ''}${selectedOption.option}`,
    ]);
  };

  return {
    locations: locationTabData?.locations ?? ([] as ILocationListing[]),
    isLocationTabDataLoading,
    selectedSortOption,
    onSortChangeHandle,
    totalCount,
    totalPages,
    page,
    handlePageChange,
    rowsPerPage: rowsPerPageParam,
    onRowsPerPageChange,
    getParamsValue,
    activeFilters,
    assignLocationGroupModal,
    confirmLocationGroupModal,
    previewLocationGroupsModal,
    locationsContainOutdoorType,
    searchParams: location.search,
    onChangeParams,
  };
};
